import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { API_BASE, formatApiError } from "../../components/AuthContext";
import { toast } from "sonner";
import { Inbox, Upload, Check, Loader2, ArrowLeft } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function PortalFilesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/portal/file-requests`)
      .then((r) => setRows(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-10 py-12 max-w-[1100px]" data-testid="portal-files-list">
      <div className="mb-8">
        <div className="overline mb-2 text-[var(--accent)]">From your accountant</div>
        <h1 className="font-heading text-4xl sm:text-5xl leading-tight mb-2" style={{ fontWeight: 500 }}>
          File <em className="italic" style={{ color: "var(--accent)" }}>requests</em>
        </h1>
        <p className="text-[var(--ink-2)] max-w-2xl">
          Documents your accountant needs from you. Upload securely — everything stays inside your portal.
        </p>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-[var(--border)] p-12 text-center" data-testid="portal-files-empty"
             style={{ background: "var(--paper)" }}>
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--subtle)] flex items-center justify-center">
            <Inbox size={22} className="text-[var(--ink-3)]" />
          </div>
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>You&apos;re all caught up</div>
          <div className="text-[var(--ink-3)] text-sm max-w-sm mx-auto">
            When your accountant needs documents from you, requests will land here.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((fr) => {
            const required = fr.items.filter((i) => i.required !== false);
            const done = required.filter((i) => i.document_id).length;
            return (
              <Link
                key={fr.file_request_id}
                to={`/portal/files/${fr.file_request_id}`}
                data-testid={`portal-file-row-${fr.file_request_id}`}
                className="card p-5 block hover:border-[var(--ink)] transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-heading text-lg" style={{ fontWeight: 500 }}>{fr.title}</div>
                    {fr.description && <div className="text-[13px] text-[var(--ink-3)] mt-1">{fr.description}</div>}
                    <div className="text-[12px] text-[var(--ink-3)] font-mono mt-2">
                      {done}/{required.length} uploaded · {fr.due_at ? `due ${fmtDate(fr.due_at)}` : "no deadline"}
                    </div>
                  </div>
                  <span className="badge" style={{
                    background: fr.status === "completed" ? "var(--moss)" : "var(--accent)", color: "var(--paper)"
                  }}>{fr.status}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function PortalFileRequest() {
  const { id } = useParams();
  const [fr, setFr] = useState(null);
  const [uploading, setUploading] = useState(null);

  const reload = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/portal/file-requests/${id}`);
    setFr(r.data);
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const upload = async (idx, file) => {
    if (!file) return;
    setUploading(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_BASE}/portal/file-requests/${id}/items/${idx}/upload`, fd);
      await reload();
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  if (!fr) return <div className="p-12 text-[var(--ink-3)]">Loading…</div>;

  return (
    <div className="px-10 py-12 max-w-[820px]" data-testid="portal-file-request-page">
      <Link to="/portal/files" className="inline-flex items-center gap-1 text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)] mb-4">
        <ArrowLeft size={12} /> Back to file requests
      </Link>
      <div className="overline mb-2">File request</div>
      <h1 className="font-heading text-3xl mb-2" style={{ fontWeight: 500 }}>{fr.title}</h1>
      {fr.description && <p className="text-[var(--ink-2)] mb-6">{fr.description}</p>}
      <div className="text-[12px] font-mono text-[var(--ink-3)] mb-8">
        Status: <span className="text-[var(--ink)]">{fr.status}</span>
        {fr.due_at && <> · Due {fmtDate(fr.due_at)}</>}
      </div>

      <div className="space-y-3">
        {fr.items.map((item, idx) => (
          <div key={`item-${idx}-${item.name}`} className="card p-5 flex items-center justify-between gap-4" data-testid={`portal-file-item-${idx}`}>
            <div>
              <div className="text-[15px] font-medium">{item.name}</div>
              <div className="text-[12px] text-[var(--ink-3)] mt-1">
                {item.required ? "Required" : "Optional"}
                {item.uploaded_at && <> · Uploaded {fmtDate(item.uploaded_at)}</>}
              </div>
            </div>
            {item.document_id ? (
              <span className="inline-flex items-center gap-1 text-[var(--moss)] text-[13px]">
                <Check size={14} /> Uploaded
              </span>
            ) : (
              <label className={`btn-primary cursor-pointer ${uploading === idx ? "opacity-60" : ""}`} data-testid={`portal-file-upload-${idx}`}>
                {uploading === idx ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading === idx ? "Uploading…" : "Upload"}
                <input
                  type="file"
                  className="hidden"
                  disabled={uploading === idx}
                  onChange={(e) => upload(idx, e.target.files?.[0])}
                />
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
